import React, { useState } from "react";
import axios from "../../axiosConfig";
import { formatValue, parseFormat } from "./financialFormat";
import "./CompanyCard.css";

const EMPTY = {
  financial_id: null,
  name: "",
  format: "{value}",
  eikon_code_actual: "",
  eikon_code_estimate: "",
  bigger_is_better: true,
};

const PREVIEW_VALUE = 1234.5678;
// For {value:m} formats: one value that's 0dp on the note and one that's 1dp
const PREVIEW_MILLIONS = [1234000000, 123400000];

// Create a financial, or pick an existing one to edit or delete.
// onChanged(catalogue) is called with the updated list of financials.
const FinancialModal = ({ financials = [], onClose, onChanged }) => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  let formatError = "";
  let preview = "";
  try {
    const { millions, brackets } = parseFormat(form.format);
    const previewValues = millions ? [...PREVIEW_MILLIONS] : [PREVIEW_VALUE];
    if (brackets) previewValues.push(-previewValues[previewValues.length - 1]);
    preview = previewValues
      .map((v) => `${v.toLocaleString("en-GB")} shows as ${formatValue(v, form.format)}`)
      .join(" and ");
  } catch (e) {
    formatError = e.message;
  }

  const pick = (financialId) => {
    const financial = financials.find((f) => f.financial_id === Number(financialId));
    setForm(
      financial
        ? {
            ...financial,
            eikon_code_actual: financial.eikon_code_actual ?? "",
            eikon_code_estimate: financial.eikon_code_estimate ?? "",
            bigger_is_better: Boolean(financial.bigger_is_better),
          }
        : EMPTY,
    );
    setError("");
  };

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const submit = async (action) => {
    if (
      action === "delete" &&
      !window.confirm(`Delete ${form.name}? This can't be undone.`)
    ) {
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const response = await axios.post(`/api/financials/${action}/`, form, {
        withCredentials: true,
      });
      onChanged(response.data.financials);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${action} the financial.`);
    } finally {
      setSubmitting(false);
    }
  };

  const editing = form.financial_id !== null;

  return (
    <div className="modal-overlay">
      <div className="modal-box">
        <h3>{editing ? "Edit financial" : "New financial"}</h3>
        <div className="question-form">
          <select
            value={form.financial_id ?? ""}
            onChange={(e) => pick(e.target.value)}
          >
            <option value="">New financial</option>
            {[...financials]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((f) => (
                <option key={f.financial_id} value={f.financial_id}>
                  Edit: {f.name}
                </option>
              ))}
          </select>

          <label>Name (row label in the portal and on the note):</label>
          <input
            type="text"
            maxLength={128}
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            placeholder="e.g. Revenue"
          />

          <label>Format:</label>
          <input
            type="text"
            maxLength={32}
            value={form.format}
            onChange={(e) => setField("format", e.target.value)}
            placeholder="{value}"
          />
          <small className={formatError ? "form-error" : "form-hint"}>
            {formatError ||
              `On the note, ${preview}. Use {value:m} for millions (0dp from 1,000m, else 1dp), {value:.1f} or {value:m.1f} for fixed decimals, and {value} as stored. End it in () for brackets on negatives, e.g. {value:m()}. A format ending in % changes in bps.`}
          </small>

          <label>Eikon code (actuals):</label>
          <input
            type="text"
            maxLength={128}
            value={form.eikon_code_actual}
            onChange={(e) => setField("eikon_code_actual", e.target.value)}
            placeholder="e.g. TR.Revenue"
          />

          <label>Eikon code (estimates):</label>
          <input
            type="text"
            maxLength={128}
            value={form.eikon_code_estimate}
            onChange={(e) => setField("eikon_code_estimate", e.target.value)}
            placeholder="e.g. TR.RevenueMean"
          />

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={form.bigger_is_better}
              onChange={(e) => setField("bigger_is_better", e.target.checked)}
            />
            Bigger is better (an increase shows green in CHG)
          </label>
        </div>

        {error && <p className="form-error">{error}</p>}

        <div className="modal-actions">
          <button className="cancel-btn" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          {editing && (
            <button
              className="cancel-btn"
              onClick={() => submit("delete")}
              disabled={submitting}
            >
              Delete
            </button>
          )}
          <button
            className="confirm-btn"
            onClick={() => submit(editing ? "edit" : "create")}
            disabled={submitting || !form.name.trim() || Boolean(formatError)}
          >
            {submitting ? "Saving..." : editing ? "Save" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FinancialModal;
